import { COCLASSES } from '@/interfaces/coclass.interface'
import { getRealmId } from '@/utils/realmId'
import { useFetchGet } from './useFetch'
import { computed, ComputedRef, Ref } from 'vue'
import {
  DefaultSelections,
  FilterSettings,
  FilterType,
  RealmSettings,
  backendToFronendQuickSelect
} from '@/api-client/models/utilization/Contracts'
import { RoomCapacity } from '@/components/types'
import { defineStore } from 'pinia'
import features from '@/features'

const getRealmUrl = (baseUrl: string, realmId: string) =>
  `${baseUrl}/realms/${realmId}?hash=${import.meta.env.VITE_CONFIG_HASH}`

interface RealmStore {
  defaultSelections: ComputedRef<DefaultSelections | undefined>
  settings: Ref<RealmSettings | undefined>
  error: Ref<Error | undefined>
  filtersMap: ComputedRef<Record<FilterType, FilterSettings>>
}

export const useRealm = defineStore('realm', (): RealmStore => {
  const realmId = getRealmId()
  const baseUrl = window.appConfig.VITE_API_BASE_URL!
  const { data: realmSettings, error } = useFetchGet<RealmSettings>(
    computed(() => (realmId ? getRealmUrl(baseUrl, realmId) : undefined))
  )
  function findFilterDefaultValue<T>(
    filters: FilterSettings[] | undefined,
    type: string
  ): T | undefined {
    return filters?.find((f) => f.type === type)?.defaultValue
  }

  const defaultSelections = computed<DefaultSelections | undefined>(
    (): DefaultSelections | undefined => {
      const utilization = realmSettings.value?.config?.utilization
      const coClasses: string[] =
        findFilterDefaultValue<string[]>(utilization?.filters, 'CO_CLASSES') ?? []
      const onlyBusinessHours: boolean =
        findFilterDefaultValue<boolean>(utilization?.filters, 'ONLY_BUSINESS_HOURS') ?? false
      const onlyBusinessDays: boolean =
        findFilterDefaultValue<boolean>(utilization?.filters, 'ONLY_BUSINESS_DAYS') ?? false
      const onlyBookable: boolean =
        findFilterDefaultValue<boolean>(utilization?.filters, 'ONLY_BOOKABLE') ?? false
      const numberOfSeats: RoomCapacity[] | undefined = findFilterDefaultValue<RoomCapacity[]>(
        utilization?.filters,
        'NUMBER_OF_SEATS'
      )
      const roomTypes = coClasses.flatMap((c) => COCLASSES.find((cc) => cc.coclass === c) || [])
      const defaultQuickSelect = backendToFronendQuickSelect(
        utilization?.calendar?.quickSelect || 'LAST_30_DAYS'
      )

      return {
        coClasses,
        onlyBusinessHours,
        onlyBusinessDays,
        onlyBookable,
        numberOfSeats,
        roomTypes,
        defaultQuickSelect
      }
    }
  )

  const filtersMap = computed<Record<FilterType, FilterSettings>>(
    () =>
      Object.fromEntries(
        realmSettings.value?.config?.utilization.filters.map((x) => [x.type, x]) || []
      ) as Record<FilterType, FilterSettings>
  )

  const computedRealmSettings = computed<RealmSettings | undefined>(() =>
    realmSettings.value
      ? import.meta.env.VITE_LOCAL === 'true'
        ? {
            ...realmSettings.value,
            features: features
          }
        : {
            ...realmSettings.value,
            // Use the new feature config if it exists, otherwise use the old one
            features: realmSettings.value?.featureConfig ?? realmSettings.value?.features
          }
      : undefined
  )

  return { defaultSelections, settings: computedRealmSettings, error, filtersMap }
})
